/**
 * FP3-112 — Shared provider-workspace page shell.
 * FP3-112F — Added `variant` prop so operational pages that are
 *            forms / lists / articles can opt into a tighter cap
 *            without bringing back the old `max-w-6xl mx-auto`
 *            narrow-centered antipattern. Every variant stays
 *            LEFT-aligned to the sidebar (no `mx-auto`); extra
 *            viewport width becomes a calm right-side margin.
 *
 * Variants:
 *   • "default" — 1600/xl:1760/2xl:none. Used by pages the user has
 *                 already validated at the wider width: Today,
 *                 Caseload (self-caps to 1400 internally). This is
 *                 the historical FP3-112 cap.
 *   • "compact" — max-w-[1280px]. Reads best for lists, forms, and
 *                 article-style content: Progress, Reports,
 *                 Notifications, Help Center, Contact.
 *   • "table"   — max-w-[1400px]. Data tables + operational lists
 *                 that need a bit more room but still shouldn't
 *                 stretch edge to edge: Workboard, Reference Library.
 *
 * Not used on:
 *   • Sidebar itself (handled in AppLayout)
 *   • Public / auth / marketing / Explore pages
 *   • Admin console pages (own wider shells)
 *   • Student Workspace (uses PageShell + its own 1360 inner cap
 *     via FP3-112D)
 *   • Schedule pages (per-tab shell via FP3-112E — 1520 canvas /
 *     1360 form)
 *
 * Do NOT add page-level `mx-auto` at any of these caps — content
 * is intentionally left-aligned so the sidebar and working area
 * share a common left boundary.
 */
const VARIANT_MAX_WIDTHS = {
  default: "max-w-[1600px] xl:max-w-[1760px] 2xl:max-w-none",
  compact: "max-w-[1280px]",
  table: "max-w-[1400px]",
};

export default function PageShell({
  children,
  className = "",
  testId,
  variant = "default",
  ...rest
}) {
  const maxWidth = VARIANT_MAX_WIDTHS[variant] || VARIANT_MAX_WIDTHS.default;
  return (
    <div
      data-testid={testId}
      className={`px-6 py-6 ${maxWidth} animate-fade-in ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}
